<template>
  <div class="min-h-screen bg-canvas flex items-center justify-center py-10 px-5">
    <div class="w-full max-w-[420px]">
      <loader v-if="isLoading"></loader>
      <div v-else class="bg-white border border-border rounded-[20px] px-9 pt-10 pb-8">
        <div class="text-center mb-8">
          <img src="/static/images/unicorn.png" alt="Unicorn Visa" width="48" class="rounded-full mb-5 mx-auto block">
          <h1 class="text-[22px] font-bold text-ink tracking-[-0.02em] m-0 mb-1.5">Log in</h1>
        </div>

        <validation-observer ref="observer" v-slot="{ handleSubmit }">
          <form @submit.stop.prevent="handleSubmit(signIn)" class="flex flex-col gap-4">
            <validation-provider
              name="E-mail"
              :rules="{ email: true, required: true }"
              v-slot="validationContext"
            >
              <div>
                <label class="text-[13px] font-semibold text-ink mb-1.5 block" for="email">Email</label>
                <input id="email"
                  type="email"
                  class="w-full h-11 border border-border-hover rounded-xl text-sm text-ink px-3.5 transition-[border-color,box-shadow] duration-150 bg-[#fafafa] box-border focus:border-primary focus:shadow-[0_0_0_3px_rgba(124,91,234,0.1)] focus:bg-white placeholder:text-placeholder"
                  v-model="email"
                  placeholder="you@company.com"
                  :class="{ '!border-[#e53935]': getValidationState(validationContext) === false }">
                <span v-if="validationContext.errors[0]" class="block text-xs text-[#e53935] mt-1">{{ validationContext.errors[0] }}</span>
              </div>
            </validation-provider>

            <validation-provider
              name="password"
              :rules="{required: true, min: 6 }"
              v-slot="validationContext"
            >
              <div>
                <div class="flex justify-between items-baseline">
                  <label class="text-[13px] font-semibold text-ink mb-1.5 block" for="password">Password</label>
                  <router-link :to="{ name: 'reset' }" class="text-[13px] text-primary no-underline hover:opacity-80 hover:no-underline">Forgot?</router-link>
                </div>
                <input id="password"
                  type="password"
                  class="w-full h-11 border border-border-hover rounded-xl text-sm text-ink px-3.5 transition-[border-color,box-shadow] duration-150 bg-[#fafafa] box-border focus:border-primary focus:shadow-[0_0_0_3px_rgba(124,91,234,0.1)] focus:bg-white placeholder:text-placeholder"
                  v-model="password"
                  placeholder="Enter your password"
                  :class="{ '!border-[#e53935]': getValidationState(validationContext) === false }">
                <span v-if="validationContext.errors[0]" class="block text-xs text-[#e53935] mt-1">{{ validationContext.errors[0] }}</span>
              </div>
            </validation-provider>

            <div v-if="error" class="bg-[#fff5f5] border border-[rgba(255,69,61,0.2)] rounded-xl py-2.5 px-3.5 mb-4">
              <p v-if="error.code === 'auth/wrong-password'" class="m-0 text-[13px] text-[#e53935] font-medium">Wrong password</p>
              <p v-else class="m-0 text-[13px] text-[#e53935] font-medium">{{ error.message }}</p>
            </div>

            <button type="submit" class="w-full h-11 border-0 rounded-full text-sm font-semibold cursor-pointer transition-[opacity,box-shadow] duration-150 flex items-center justify-center gap-2 bg-ink text-white hover:bg-[#2a2b2d] hover:shadow-[0_2px_8px_rgba(0,0,0,0.1)] disabled:opacity-60 disabled:cursor-not-allowed" :disabled="isFormLoading">
              Sign in
            </button>

            <div class="flex items-center my-1">
              <div class="flex-1 h-px bg-border"></div>
              <span class="px-3.5 text-xs text-placeholder uppercase tracking-[0.06em] font-medium">or</span>
              <div class="flex-1 h-px bg-border"></div>
            </div>

            <button type="button" class="w-full h-11 rounded-full text-sm font-semibold cursor-pointer transition-[opacity,box-shadow] duration-150 flex items-center justify-center gap-2 bg-white text-ink border border-border-hover hover:border-[#d0ccd5] hover:shadow-[0_1px_4px_rgba(0,0,0,0.06)]" @click.prevent="googleSignIn">
              <img src="/static/images/icons/google.png" width="18" alt="">
              Continue with Google
            </button>
          </form>
        </validation-observer>

        <div class="text-center mt-6 pt-5 border-t border-border">
          <p class="m-0 text-sm text-muted">Don't have an account? <router-link :to="{ name: 'register' }" class="text-primary font-semibold no-underline hover:opacity-80 hover:no-underline">Sign up</router-link></p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import firebase from '@/firebase'
export default {
  name: 'login',
  data () {
    return {
      isLoading: null,
      email: null,
      password: null
    }
  },
  computed: {
    ...mapState({
      isFormLoading: state => state.auth.loading
    }),
    user () {
      return this.$store.getters.getUser
    },
    error () {
      return this.$store.getters.getError
    }
  },
  mounted () {
    this.isLoading = true
    setTimeout(() => {
      this.isLoading = false
    }, 300)

    if (this.user !== null && this.user !== undefined) {
      this.$router.push({ name: 'admin-applicants' }).catch(err => err)
    }
  },
  methods: {
    googleSignIn () {
      this.$store.dispatch('GOOGLE_SIGN_IN').then((response) => {
        this.$router.push({ name: 'admin-applicants' }).catch(err => err)
      }).catch(error => {
        console.error('Google sign-in failed:', error)
        this.hasError = true
      })
    },
    getValidationState ({ dirty, validated, valid = null }) {
      return dirty || validated ? valid : null
    },
    signIn () {
      const user = {
        email: this.email,
        password: this.password
      }
      this.$store.dispatch('LOGIN_USER', user)
    }
  },
  created () {
    firebase.auth().onAuthStateChanged((user) => {
      if (user) {
        this.$store.dispatch('AUTO_SIGN_IN', user)
      }
    })
  },
  watch: {
    user (value) {
      if (value !== null && value !== undefined) {
        this.$router.push({ name: 'admin-applicants' }).catch(err => err)
      }
    }
  }
}
</script>
